
import React, { useState, useEffect } from 'react';
import type { SubscriptionTier, UserProfile, Organization } from '@types';
import { openCheckout, getPaddleTierPriceIds } from '../../services/paddleService';
import { db } from '../../firebaseConfig';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import SubscriberAgreementModal, { type AgreementRecord } from './SubscriberAgreementModal';

interface SubscriptionSetupProps {
    orgId: string;
    onSubscriptionSuccess: (org: Organization) => void;
    onLogout: () => void;
    preselectedPlan?: SubscriptionTier | null;
    currentUser?: UserProfile | null;
    onNavigate?: (page: string) => void;
}

const SubscriptionSetup: React.FC<SubscriptionSetupProps> = ({ orgId, onSubscriptionSuccess, onLogout, preselectedPlan, currentUser, onNavigate }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [paddleError, setPaddleError] = useState<{ title: string; message: React.ReactNode } | null>(null);
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [pendingPlan, setPendingPlan] = useState<SubscriptionTier | null>(null);
    const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
    
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier>((preselectedPlan || 'individual') as SubscriptionTier);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
    const planToDisplay = selectedPlan;

    const PRICES = {
        individual: { monthly: 99, annual: 79, annualTotal: 948 },
        business:   { monthly: 349, annual: 279, annualTotal: 3348 },
        agency:     { monthly: 649, annual: 519, annualTotal: 6228 },
    } as const;

    useEffect(() => {
        if (preselectedPlan) setSelectedPlan(preselectedPlan);
    }, [preselectedPlan]);
    
    const handleSuccess = (plan: SubscriptionTier) => {
        // This function would typically call a backend to update the organization's subscription status
        console.log(`Successfully subscribed org ${orgId} to the ${plan} plan.`);
        const updatedOrg: Organization = {
            id: orgId,
            name: 'IntelOptics', // This would be fetched from the backend
            ownerId: currentUser?.uid || '',
            tier: plan,
            maxSeats: 1, // Business starts with 1 admin, pay for extras
            seatsUsed: 1,
            totalAdmins: 1,
            totalUsers: 0,
            extraAdminSeats: 0,
            extraUserSeats: 0,
            createdAt: new Date().toISOString(),
        };
        onSubscriptionSuccess(updatedOrg);
    };

    const handleSelect = async (plan: SubscriptionTier) => {
        setPaddleError(null);

        let priceId = '';
        try {
            const prices = await getPaddleTierPriceIds();
            priceId = billingCycle === 'annual'
                ? (plan === 'business' ? prices.annualBusiness : plan === 'agency' ? prices.annualAgency : prices.annualIndividual)
                : (plan === 'business' ? prices.business : plan === 'agency' ? prices.agency : prices.individual);
        } catch (error: any) {
            setPaddleError({ title: 'Billing Configuration Error', message: error.message || 'Product mappings failed loading.' });
            return;
        }

        if (!priceId || plan === 'agency') {
            setPaddleError({
                title: 'Agency Platform — Coming Soon',
                message: 'Our Agency tier is currently in development. Look out for the future release of our Agency Business Model coming soon. In the meantime, our Individual and Business tiers include the full intelligence suite.'
            });
            return;
        }

        // Show agreement modal — user must sign before Paddle checkout
        setPendingPlan(plan);
        setPendingPriceId(priceId);
        setShowAgreementModal(true);
    };

    const handleAgreementAccepted = async (record: AgreementRecord) => {
        if (!pendingPlan || !pendingPriceId) return;
        setIsProcessing(true);
        setShowAgreementModal(false);

        // 1. Save enriched agreement record to Firestore
        try {
            const orgRef = doc(db, 'organizations', orgId);
            await updateDoc(orgRef, {
                legalAgreements: arrayUnion(record)
            });
            console.log('📝 Agreement record saved:', record.version, record.agreementHash);
        } catch (fireError: any) {
            console.warn('⚠️ Could not save agreement record:', fireError);
            // In production this is non-fatal — Paddle still handles payment
        }

        // 2. Launch Paddle checkout
        try {
            console.log(`🛠️ Launching Paddle checkout for ${pendingPlan}...`);
            await openCheckout({
                items: [{ priceId: pendingPriceId, quantity: 1 }],
                customer: { email: currentUser?.email || '' },
                customData: {
                    organization_id: orgId,
                    user_id: currentUser?.uid,
                    plan_type: pendingPlan
                },
                settings: {
                    displayMode: 'overlay',
                    theme: 'dark',
                    showAddDiscounts: false
                }
            });
        } catch (err: any) {
            console.error('Paddle checkout failed:', err);
            setPaddleError({
                title: 'Checkout Error',
                message: err.message || 'Failed to initialize Paddle checkout. Please try again.'
            });
            setIsProcessing(false);
        }
    };

    return (
        <>
        <div className="min-h-screen bg-slate-950 flex flex-col items-center py-12 px-4 animate-fade-in relative overflow-hidden">
            {/* Background ambient glows */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none opacity-20">
                <div className="absolute top-20 left-20 w-72 h-72 bg-blue-600 rounded-full blur-[120px]"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-600 rounded-full blur-[150px]"></div>
            </div>

            <div className="text-center mb-16 max-w-2xl mt-12 relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/50 border border-slate-800 text-blue-400 text-[10px] font-bold uppercase tracking-widest mb-6">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                    Identity Verified
                </div>
                <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight">Complete Your Setup</h2>
                <p className="text-lg text-slate-400 leading-relaxed">
                    Account <span className="text-blue-400 font-mono font-bold">[{currentUser?.email?.split('@')[0]}]</span> initialized. 
                    Finalize your selection to activate the Intelligence Suites.
                </p>
            </div>

            <div className="flex bg-slate-900/60 p-1.5 rounded-2xl border border-slate-800 mb-8 z-10">
                <button 
                    onClick={() => setSelectedPlan('individual')}
                    className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${selectedPlan === 'individual' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    Individual
                </button>
                <button 
                    onClick={() => setSelectedPlan('business')}
                    className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${selectedPlan === 'business' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    Business
                </button>
                {/* Agency: Coming Soon — tab is disabled, not removed */}
                <div className="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 flex items-center gap-2 cursor-not-allowed select-none" title="Coming soon">
                    Agency
                    <span className="text-[8px] bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded-full border border-slate-700">Coming Soon</span>
                </div>
            </div>

            {/* Billing cycle toggle */}
            <div className="flex items-center gap-3 mb-8 z-10">
                <span className={`text-xs font-black uppercase tracking-widest transition-colors ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
                <button
                    onClick={() => setBillingCycle(c => c === 'monthly' ? 'annual' : 'monthly')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        billingCycle === 'annual' ? 'bg-emerald-600' : 'bg-slate-700'
                    }`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                </button>
                <span className={`text-xs font-black uppercase tracking-widest transition-colors ${billingCycle === 'annual' ? 'text-emerald-400' : 'text-slate-500'}`}>
                    Annual
                    <span className="ml-2 bg-emerald-500/20 text-emerald-400 text-[9px] px-2 py-0.5 rounded-full border border-emerald-500/30">Save 20%</span>
                </span>
            </div>

            <div className="w-full max-w-2xl relative z-10">
                <div className={`group relative bg-slate-900/40 backdrop-blur-xl rounded-[2.5rem] border transition-all duration-500 p-10 md:p-12 flex flex-col overflow-hidden ${
                    planToDisplay === 'agency'   ? 'border-violet-500/40 shadow-[0_0_50px_rgba(139,92,246,0.15)]' :
                    planToDisplay === 'business' ? 'border-blue-500/40 shadow-[0_0_50px_rgba(59,130,246,0.15)]' :
                    'border-slate-800 shadow-2xl'
                }`}>
                    
                    {/* Glass highlight effect */}
                    <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent to-transparent opacity-50 ${
                        planToDisplay === 'agency' ? 'via-violet-500/50' : 'via-blue-500/50'
                    }`}></div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-3xl font-black text-white tracking-tight">
                                    {planToDisplay === 'agency' ? 'Agency Operations' : planToDisplay === 'business' ? 'Business Intelligence' : 'Individual Professional'}
                                </h3>
                                {planToDisplay === 'business' && (
                                    <span className="bg-blue-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Standard</span>
                                )}
                                {planToDisplay === 'agency' && (
                                    <span className="bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Agency</span>
                                )}
                            </div>
                            <p className="text-slate-400 text-sm font-medium">
                                {planToDisplay === 'agency'
                                    ? 'Manage multiple client programmes with white-label intelligence delivery.'
                                    : planToDisplay === 'business'
                                    ? 'Centralised intelligence workflows for high-performance risk teams.'
                                    : 'Advanced OSINT & Geopolitical toolset for professional practitioners.'}
                            </p>
                        </div>
                        <div className="text-left md:text-right">
                            <div className="flex items-baseline md:justify-end gap-2">
                                <span className="text-5xl font-black text-white">
                                    ${billingCycle === 'annual'
                                        ? PRICES[planToDisplay as keyof typeof PRICES]?.annual ?? 99
                                        : PRICES[planToDisplay as keyof typeof PRICES]?.monthly ?? 99}
                                </span>
                                <span className="text-slate-500 font-bold uppercase text-[10px] tracking-widest">/mo</span>
                            </div>
                            {billingCycle === 'annual' && (
                                <div className="mt-1 text-[10px] font-bold text-emerald-400 uppercase tracking-[0.15em]">
                                    ${PRICES[planToDisplay as keyof typeof PRICES]?.annualTotal ?? 948}/yr — Save 20%
                                </div>
                            )}
                            <div className="mt-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest md:text-right">
                                All prices exclusive of taxes
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5 mb-12">
                        {planToDisplay === 'agency' ? (
                            <>
                                <FeatureItem text="Everything in Business, Plus:" highlight />
                                <FeatureItem text="Agency Dashboard & Client Management" />
                                <FeatureItem text="Up to 5 Client Workspaces (base)" />
                                <FeatureItem text="Client-Scoped SRM Project Tracking" />
                                <FeatureItem text="White-Label Report Branding" />
                                <FeatureItem text="Client Portal Access (Phase 2)" />
                                <FeatureItem text="Priority Onboarding Support" />
                            </>
                        ) : planToDisplay === 'business' ? (
                            <>
                                <FeatureItem text="Everything in Professional, Plus:" highlight />
                                <FeatureItem text="EP, CMP, & HSSE Implementation" />
                                <FeatureItem text="VCMC Simulations & Training" />
                                <FeatureItem text="Business Resiliency Hub" />
                                <FeatureItem text="Advanced Operations Dashboard" />
                                <FeatureItem text="Custom Branding & White-label" />
                            </>
                        ) : (
                            <>
                                <FeatureItem text="SRM Intelligence Core" />
                                <FeatureItem text="Global Risk Map & Daily Feed" />
                                <FeatureItem text="SRM Architect & Command Center" />
                                <FeatureItem text="Security Risk Management Suite" />
                                <FeatureItem text="Standard Report Generation" />
                                <FeatureItem text="Unlimited Intelligence Drafting" />
                            </>
                        )}
                    </div>

                    {paddleError && (
                        <div className="mb-8 p-5 bg-red-500/10 text-red-400 rounded-2xl border border-red-500/20 flex gap-4 animate-shake">
                            <div className="shrink-0 h-6 w-6 flex items-center justify-center bg-red-500 rounded-lg text-slate-950 font-bold">!</div>
                            <div>
                                <h4 className="font-bold text-sm mb-1">{paddleError.title}</h4>
                                <div className="text-xs font-medium opacity-80">{paddleError.message}</div>
                            </div>
                        </div>
                    )}

                    <div className="mt-auto flex flex-col sm:flex-row gap-5">
                        <button 
                            onClick={onLogout}
                            disabled={isProcessing}
                            className="w-full sm:w-auto px-8 py-4 rounded-2xl font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-all border border-slate-800 flex items-center justify-center gap-2 group disabled:opacity-50"
                        >
                            <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 8.959 8.959 0 01-9 9m9-9H3"/></svg>
                            Log Out
                        </button>
                        <div className="flex-1 flex flex-col gap-4">
                            <div className="text-[10px] text-slate-500 font-medium px-1">
                                By clicking Activate Suite, you agree to the <button onClick={() => onNavigate?.('subscription-agreement')} className="text-blue-400 hover:underline">User Subscription Agreement</button>.
                            </div>
                            <button 
                                onClick={() => handleSelect(planToDisplay)}
                            disabled={isProcessing}
                            className="w-full sm:flex-1 py-4 px-8 rounded-2xl font-black text-white transition-all bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-[0_10px_30px_rgba(59,130,246,0.3)] hover:shadow-blue-500/40 disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-3 uppercase tracking-widest text-sm"
                        >
                             {isProcessing ? (
                                <span className="flex items-center gap-3">
                                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Initializing...
                                </span>
                             ) : (
                                <>
                                    Activate Suite
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
                                </>
                             )}
                        </button>
                    </div>
                </div>

                    <div className="mt-6 flex items-center justify-center gap-2 opacity-60">
                         <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                            You will be redirected to <span className="text-blue-400">Paddle</span> for secure payment processing
                        </p>
                    </div>
                </div>
                
            </div>

            <footer className="mt-20 py-8 opacity-40 hover:opacity-100 transition-opacity">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.5em]">IntelOptics Secured Transaction Terminal</p>
            </footer>
        </div>

        {/* ── Agreement Modal ── */}
        {showAgreementModal && pendingPlan && (pendingPlan === 'individual' || pendingPlan === 'business') && (
            <SubscriberAgreementModal
                tier={pendingPlan}
                userEmail={currentUser?.email || ''}
                userUid={currentUser?.uid || ''}
                billingLabel={
                    pendingPlan === 'individual'
                        ? `Individual Professional — $${billingCycle === 'annual' ? PRICES.individual.annual : PRICES.individual.monthly}/${billingCycle === 'annual' ? 'mo (billed annually)' : 'month'}`
                        : `Business Intelligence — $${billingCycle === 'annual' ? PRICES.business.annual : PRICES.business.monthly}/${billingCycle === 'annual' ? 'mo (billed annually)' : 'month'}`
                }
                onAccepted={handleAgreementAccepted}
                onDeclined={() => {
                    setShowAgreementModal(false);
                    setPendingPlan(null);
                    setPendingPriceId(null);
                }}
            />
        )}
        </>
    );
};

const FeatureItem: React.FC<{ text: string; highlight?: boolean }> = ({ text, highlight }) => (
    <div className="flex items-center gap-4 group/item">
        <div className={`h-6 w-6 rounded-lg flex items-center justify-center transition-all ${
            highlight
                ? 'bg-violet-500/20 text-violet-400 group-hover/item:bg-violet-500 group-hover/item:text-white'
                : 'bg-blue-500/10 text-blue-500 group-hover/item:bg-blue-500 group-hover/item:text-white'
        }`}>
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
        <span className={`text-xs font-bold group-hover/item:text-slate-200 transition-colors ${
            highlight ? 'text-violet-400' : 'text-slate-400'
        }`}>{text}</span>
    </div>
);

export default SubscriptionSetup;
